import { AsyncPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal, ViewChild } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { StatsCampaignType } from "@front/m19-api-client";
import {
  ACOS,
  AD_CONVERSIONS,
  AD_SALES,
  CLICK_THROUGH_RATE,
  CLICKS,
  CONVERSION_RATE,
  COST,
  CPC,
  IMPRESSIONS,
  Metric,
  MetricType,
  PALETTE,
  ROAS,
} from "@front/m19-metrics";
import { AdStatsEx, StrategyEx } from "@front/m19-models";
import { CAMPAIGN_TYPE_NAME } from "@front/m19-models/dataset/BarChartDataset";
import {
  AccountSelectionService,
  adStatsGroupBy,
  MetricsSelectorPage,
  MetricsSelectorKey,
  StatsService,
  StrategyCache,
  UserSelectionService,
} from "@front/m19-services";
import {
  ChartConfig,
  IButtonDirective,
  IChartComponent,
  IIconComponent,
  ITooltipDirective,
  Option,
} from "@front/m19-ui";
import { DateAggregation, mergeSeveralDates, MetricTitlePipe, sumAdStats } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ChartLayoutComponent } from "@m19-board/containers/chart-layout/chart-layout.component";
import { getCsvFileName } from "@m19-board/grid-config/grid-config";
import { MetricSelectorComponent } from "@m19-board/insights/metric-selector/metric-selector.component";
import {
  AdType,
  CompactFilterComponent,
  Filter,
  FilterCategory,
} from "@m19-board/shared/compact-filter/compact-filter.component";
import { CompactFilterService } from "@m19-board/shared/compact-filter/compact-filter.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { BehaviorSubject, combineLatest, Observable, of, tap } from "rxjs";
import { map, share, shareReplay, startWith, switchMap } from "rxjs/operators";
import {
  ActivityEventAnnotationsService,
  ActivityEventType,
} from "../../activities/activity-event-annotations.service";
import { StrategyStats, toStrategyStats } from "../../models/Metric";
import { AdvertisingStatsGridComponent, GlobalStrategyStats } from "./advertising-stats-grid.component";

export const CAMPAIGN_TYPE_COLOR: Record<StatsCampaignType, string> = {
  [StatsCampaignType.SP]: PALETTE[0],
  [StatsCampaignType.SB]: PALETTE[1],
  [StatsCampaignType.SD]: PALETTE[2],
  [StatsCampaignType.SDR]: PALETTE[3],
  [StatsCampaignType.DSP]: PALETTE[4],
};

@UntilDestroy()
@Component({
  templateUrl: "./advertising-stats.component.html",
  imports: [
    MetricSelectorComponent,
    TranslocoDirective,
    AsyncPipe,
    IButtonDirective,
    ITooltipDirective,
    AdvertisingStatsGridComponent,
    ChartLayoutComponent,
    CompactFilterComponent,
    IIconComponent,
    IChartComponent,
    MetricTitlePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvertisingStatsComponent implements OnInit {
  private userSelectionService = inject(UserSelectionService);
  private accountSelectionService = inject(AccountSelectionService);
  private activityEventAnnotationsService = inject(ActivityEventAnnotationsService);
  private translocoService = inject(TranslocoService);
  private statsService = inject(StatsService);

  private readonly strategyCache = inject(StrategyCache);
  private readonly compactFilterService = inject(CompactFilterService);

  readonly STRATEGY_GRID_KEY = "overviewGrid2";

  readonly loading = signal(true);
  readonly localStorageKey: MetricsSelectorKey = { page: MetricsSelectorPage.overview };
  readonly CampaignTypes = [StatsCampaignType.SP, StatsCampaignType.SB, StatsCampaignType.SD, StatsCampaignType.DSP];
  readonly chartDisplayModeOptions: ("lineChart" | "donuts")[] = ["donuts", "lineChart"];
  chartDisplayMode: "lineChart" | "donuts";
  dateAggregation$: BehaviorSubject<DateAggregation> = new BehaviorSubject<DateAggregation>(DateAggregation.daily);
  displayEventAnnotation$ = new BehaviorSubject(false);
  allEventAnnotationTypes: Option<ActivityEventType>[] =
    this.activityEventAnnotationsService.allActivityEventTypesOptions;
  allStrategies$: Observable<Option<StrategyEx>[]> = this.activityEventAnnotationsService.allStrategiesOptions$;

  @ViewChild(AdvertisingStatsGridComponent) overviewGrid!: AdvertisingStatsGridComponent;

  readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$);

  selectedMetric$: BehaviorSubject<Metric<AdStatsEx>[]> = new BehaviorSubject<Metric<AdStatsEx>[]>([AD_SALES, COST]);
  selectedMetrics: Metric<StrategyStats>[] = [];

  mainMetric$ = this.selectedMetric$.pipe(map((m) => (m.length === 1 ? m[0] : m[1])));
  mainMetric = toSignal(this.mainMetric$, { requireSync: true });

  private readonly dailyCampaignStats$ = combineLatest([
    this.accountSelectionService.singleAccountMarketplaceSelection$,
    this.userSelectionService.dateRange$,
    this.userSelectionService.selectedCurrency$,
  ]).pipe(
    tap(() => this.loading.set(true)),
    switchMap(([am, dr, currency]) =>
      combineLatest([
        this.statsService.getDailyCampaignStats(am.accountId, am.marketplace, currency, dr[0], dr[1]),
        am.dspAdvertiser
          ? this.statsService.getDailyDspCampaignStats(
              am.accountId,
              am.marketplace,
              am.dspAdvertiserId!,
              currency,
              dr[0],
              dr[1],
            )
          : of([]),
        this.selectedCampaignTypes$,
      ]),
    ),
    map(([data, dspStats, filter]) => {
      data = [...data, ...dspStats];
      data = filter.size === 0 ? data : data.filter((d) => filter.has(d.campaignType!));
      return data;
    }),
    tap({
      next: () => this.loading.set(false),
      error: () => this.loading.set(false),
    }),
    shareReplay(1),
  );

  private readonly previousPeriodCampaignStats$ = combineLatest([
    this.accountSelectionService.singleAccountMarketplaceSelection$,
    this.userSelectionService.periodComparison$,
    this.userSelectionService.selectedCurrency$,
  ]).pipe(
    switchMap(([am, pc, currency]) => {
      if (!pc?.period) {
        return of([]);
      }
      return combineLatest([
        this.statsService.getDailyCampaignStats(am.accountId, am.marketplace, currency, pc.period[0], pc.period[1]),
        am.dspAdvertiser
          ? this.statsService.getDailyDspCampaignStats(
              am.accountId,
              am.marketplace,
              am.dspAdvertiserId!,
              currency,
              pc.period[0],
              pc.period[1],
            )
          : of([]),
        this.selectedCampaignTypes$,
      ]).pipe(
        map(([data, dspStats, filter]) => {
          data = [...data, ...dspStats];
          data = filter.size === 0 ? data : data.filter((d) => filter.has(d.campaignType!));
          return data;
        }),
      );
    }),
    shareReplay(1),
  );

  private readonly selectedCampaignTypes = signal<Set<StatsCampaignType>>(new Set(this.CampaignTypes));
  private readonly selectedCampaignTypes$: Observable<Set<StatsCampaignType>> = toObservable(
    this.selectedCampaignTypes,
  );

  readonly allStats$ = this.dailyCampaignStats$.pipe(
    map((data) =>
      Array.from(adStatsGroupBy<number>(data, (x) => x.strategyId!).values()).map((x) => {
        x.placement = undefined;
        return x;
      }),
    ),
  );

  readonly strategyStats$ = combineLatest([
    this.allStats$,
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.strategyCache.getAllStrategyIndex(am.accountId, am.marketplace)),
    ),
  ]).pipe(
    map(([data, strategyIndex]) => data.map((x) => toStrategyStats(x, strategyIndex))),
    share(),
  );
  sortedStrategyStats = toSignal(this.strategyStats$);
  readonly campaignTypeStatsFilter = computed<(stats: GlobalStrategyStats) => boolean>(() => {
    const selectedCampaignTypes = this.selectedCampaignTypes();
    return (stats: GlobalStrategyStats) => selectedCampaignTypes.has(stats.campaignType!);
  });

  private readonly metrics = toSignal(this.selectedMetric$, { requireSync: true });
  private readonly dateRange = toSignal(this.userSelectionService.selectedDateRange$, { requireSync: true });
  private readonly periodComparison = toSignal(this.userSelectionService.periodComparison$, { requireSync: true });
  private readonly dateAggregation = toSignal(this.dateAggregation$, { requireSync: true });
  readonly disableEventAnnotation = computed(() => this.dateAggregation() !== DateAggregation.daily);
  private readonly previousPeriodData = toSignal(this.previousPeriodCampaignStats$);
  private readonly eventAnnotations = toSignal(
    combineLatest([this.displayEventAnnotation$, this.dateAggregation$]).pipe(
      switchMap(([displayEventAnnotation, dateAggregation]) =>
        displayEventAnnotation && dateAggregation === DateAggregation.daily
          ? this.activityEventAnnotationsService.activityEventsAnnotations$
          : of([]),
      ),
    ),
  );

  public readonly chartConfig = computed<ChartConfig<AdStatsEx, "line">>(() => ({
    type: "line",
    aspectRatio: 3,
    aggFunctions: [mergeSeveralDates],
    metrics: this.metrics(),
    dateAggregation: this.dateAggregation(),
    dateRange: this.dateRange(),
    previousPeriodData: this.previousPeriodData(),
    previousPeriod: this.periodComparison()?.period,
    eventAnnotations: this.eventAnnotations(),
  }));
  readonly globalData = toSignal(
    this.dailyCampaignStats$.pipe(
      map((data) => {
        const globalData: AdStatsEx = {};
        for (const d of adStatsGroupBy<StatsCampaignType>(data, (x) => x.campaignType!, this.CampaignTypes).values())
          mergeSeveralDates(globalData, d);
        return globalData;
      }),
    ),
    { initialValue: {} },
  );
  readonly previousPeriodGlobalData = toSignal(
    this.previousPeriodCampaignStats$.pipe(
      map((data) => {
        const previousPeriodGlobalData: AdStatsEx = {};
        for (const d of adStatsGroupBy<StatsCampaignType>(data, (x) => x.campaignType!, this.CampaignTypes).values()) {
          if (Object.keys(d).length != 0) mergeSeveralDates(previousPeriodGlobalData, d);
        }
        return previousPeriodGlobalData;
      }),
    ),
    { initialValue: {} },
  );
  public readonly dailyCampaignStats = toSignal(this.dailyCampaignStats$.pipe(startWith([])), { requireSync: true });

  campaignTypeDonutChartConfig = computed<ChartConfig<AdStatsEx, "doughnut" | "bar">>(() => {
    const groupByFn = (x: AdStatsEx) => ({
      key: x.campaignType!,
      label: this.translocoService.translate(CAMPAIGN_TYPE_NAME[x.campaignType!]),
      color: CAMPAIGN_TYPE_COLOR[x.campaignType!],
    });

    return {
      type: this.mainMetric().type === MetricType.RATIO ? "bar" : "doughnut",
      aggFunctions: [sumAdStats],
      groupByFunctions: [groupByFn],
      metrics: [this.mainMetric()],
      legendDisplay: "side",
      valueDisplay: "ticks",
    };
  });

  strategyDonutChartConfig = computed<ChartConfig<StrategyStats, "doughnut" | "bar">>(() => {
    const groupByFn = (x: StrategyStats) => ({
      key: x.strategyName,
      label: x.strategyName,
    });

    return {
      type: this.mainMetric().type === MetricType.RATIO ? "bar" : "doughnut",
      aggFunctions: [this.addStrategyStats],
      groupByFunctions: [groupByFn],
      metrics: [this.mainMetric()],
      maxItems: 8,
      legendDisplay: "side",
      valueDisplay: "ticks",
    };
  });

  chartDisplayed = true;

  readonly filters = computed(() => [
    this.compactFilterService.getFilterWithOptions(FilterCategory.AdType, [AdType.SponsoredAds, AdType.DSP]),
  ]);

  readonly CHART_METRICS: Metric<AdStatsEx>[] = [
    AD_SALES,
    AD_CONVERSIONS,
    COST,
    ACOS,
    CLICKS,
    IMPRESSIONS,
    CLICK_THROUGH_RATE,
    CONVERSION_RATE,
    CPC,
    ROAS,
  ];

  constructor() {
    this.chartDisplayMode = this.userSelectionService.getUserOverviewChartTypePreference();
  }

  ngOnInit(): void {
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(untilDestroyed(this)).subscribe((am) => {
      if (!am.dspAdvertiserId) {
        this.selectedCampaignTypes.set(new Set(this.CampaignTypes.filter((ct) => ct !== StatsCampaignType.DSP)));
      }
    });

    this.chartDisplayed = this.userSelectionService.getUserChartDisplayedPreference(this.localStorageKey);
  }

  addStrategyStats(stats1: StrategyStats, stats2: StrategyStats): StrategyStats {
    return {
      ...stats1,
      ...sumAdStats(stats1, stats2),
    };
  }

  exportGridCsv(): void {
    const fileName = getCsvFileName(
      "strategy_stats",
      this.accountMarketplace()?.accountGroupName,
      this.accountMarketplace()?.marketplace,
      this.userSelectionService.getDateRangeStr(),
    );
    this.overviewGrid.exportGridCsv(fileName);
  }

  selectMetrics(metrics: Metric<AdStatsEx>[]): void {
    this.selectedMetric$.next(metrics);
  }

  toggleChartDisplay(displayed: boolean): void {
    this.chartDisplayed = displayed;
    this.userSelectionService.setUserChartDisplayedPreference(this.localStorageKey, displayed);
  }

  toggleChartDisplayMode(chartDisplayMode: string): void {
    this.chartDisplayMode = chartDisplayMode as "lineChart" | "donuts";
    this.userSelectionService.setUserOverviewChartTypePreference(this.chartDisplayMode);
  }

  selectAggregation(aggregation: DateAggregation): void {
    this.dateAggregation$.next(aggregation);
  }

  toggleEventAnnotation(checked: boolean): void {
    this.displayEventAnnotation$.next(checked);
  }

  applyAdTypeFilter(filters: Filter<FilterCategory.AdType>[]) {
    const values: AdType[] = filters.find((f) => f.type === FilterCategory.AdType)?.values as AdType[];
    if (!values?.length) {
      this.selectedCampaignTypes.set(new Set(this.CampaignTypes));
      return;
    }
    const campaignTypes = values.flatMap((v) => this.adTypeToCampaignTypes(v));
    this.selectedCampaignTypes.set(new Set(campaignTypes));
  }

  private adTypeToCampaignTypes(adType: AdType) {
    switch (adType) {
      case AdType.SponsoredAds:
        return [StatsCampaignType.SP, StatsCampaignType.SB, StatsCampaignType.SD];
      case AdType.DSP:
        return [StatsCampaignType.DSP];
      default:
        return [];
    }
  }
}
